import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';

type WarehouseRow = { id: number; code: string; name: string; base_role: string; by_module: Record<string, string> };
type StatusRow = { status: string; default: string; by_module: Record<string, string> };
type Source = { key: string; label: string; categories: string[]; statuses: StatusRow[] };
type Payload = {
    modules: Record<string, string>;
    roles: string[];
    category_labels: Record<string, string>;
    warehouses: WarehouseRow[];
    sources: Source[];
};

const ROLE_LABELS: Record<string, string> = {
    poslovnica: 'Poslovnica',
    veleprodaja: 'Veleprodaja',
    akcijsko: 'Akcijsko',
    samo_isporuke: 'Samo za isporuke',
    nadopuna_lagera: 'Nadopuna lagera',
};

// 133: uloga skladista i kategorija statusa zasebno po modulu 40.x. Prazno = osnovno (128 / zadano u kodu).
export function AdminGoldModuleCategoriesSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Payload | null>(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState('');

    const load = useCallback(async () => {
        try {
            setData(await api<Payload>('/admin/gold-module-categories', token));
            setError('');
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    const saveWarehouse = async (id: number, module: string, role: string | null) => {
        setSaving(`w${id}${module}`);
        try {
            await mutate(`/admin/gold-module-categories/warehouses/${id}`, token, 'PUT', { module, role });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving('');
        }
    };

    const saveStatus = async (source: string, status: string, module: string, category: string | null) => {
        setSaving(`s${source}${status}${module}`);
        try {
            await mutate('/admin/gold-module-categories/statuses', token, 'PUT', { source, status, module, category });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving('');
        }
    };

    if (!data) {
        return <div>{error ? <p className="error">{error}</p> : <p className="muted">{t('Učitavam…', 'Loading…')}</p>}</div>;
    }
    const modules = Object.entries(data.modules);
    const cell = { fontSize: '0.74rem', padding: '2px 4px', border: '1px solid #ddd', borderRadius: 3 } as const;

    return (
        <div>
            <h3>{t('133 Kategorije po modulu (40)', '133 Categories per module (40)')}</h3>
            <p className="muted" style={{ fontSize: '0.8rem' }}>
                {t(
                    'Svako skladište i svaki status u jednom modulu ima točno jednu kategoriju, pa se ista roba ne broji dvaput. Prazna ćelija znači osnovnu ulogu iz 128, odnosno zadanu kategoriju.',
                    'Each warehouse and status has exactly one category per module. An empty cell means the base role from 128 or the default category.',
                )}
            </p>
            {error ? <p className="error">{error}</p> : null}

            <h4>{t('Uloga skladišta', 'Warehouse role')}</h4>
            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Šifra', 'Code')}</th>
                            <th>{t('Naziv', 'Name')}</th>
                            <th>{t('Osnovno (128)', 'Base (128)')}</th>
                            {modules.map(([m, label]) => <th key={m} title={label}>{m}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.warehouses.map((w) => (
                            <tr key={w.id}>
                                <td><strong>{w.code}</strong></td>
                                <td>{w.name}</td>
                                <td>{ROLE_LABELS[w.base_role] ?? w.base_role}</td>
                                {modules.map(([m]) => (
                                    <td key={m}>
                                        <select
                                            value={w.by_module[m] ?? ''}
                                            disabled={saving === `w${w.id}${m}`}
                                            onChange={(e) => void saveWarehouse(w.id, m, e.target.value || null)}
                                            style={{ ...cell, fontWeight: w.by_module[m] ? 700 : 400 }}
                                        >
                                            <option value="">{t('osnovno', 'base')}</option>
                                            {data.roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>)}
                                        </select>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {data.sources.map((s) => (
                <div key={s.key}>
                    <h4>{s.label}</h4>
                    <div className="table-wrap">
                        <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Status', 'Status')}</th>
                                    <th>{t('Zadano', 'Default')}</th>
                                    {modules.map(([m, label]) => <th key={m} title={label}>{m}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {s.statuses.map((st) => (
                                    <tr key={st.status}>
                                        <td><code>{st.status}</code></td>
                                        <td>{data.category_labels[st.default] ?? st.default}</td>
                                        {modules.map(([m]) => (
                                            <td key={m}>
                                                <select
                                                    value={st.by_module[m] ?? ''}
                                                    disabled={saving === `s${s.key}${st.status}${m}`}
                                                    onChange={(e) => void saveStatus(s.key, st.status, m, e.target.value || null)}
                                                    style={{ ...cell, fontWeight: st.by_module[m] ? 700 : 400 }}
                                                >
                                                    <option value="">{t('zadano', 'default')}</option>
                                                    {s.categories.map((c) => <option key={c} value={c}>{data.category_labels[c] ?? c}</option>)}
                                                </select>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}
